import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ModalCard, useCreateModal } from 'partial/components/Modal';
import Button from 'partial/components/Button';
import Form from 'partial/components/form';
import { head, upperCase } from 'lodash';
import FormRichText from 'partial/components/form/FormRichText';
import FormInput from 'partial/components/form/FormInput';
import { useCreateContent, useUpdateContent } from '../hooks';

export const useOpenContentSetup = () => {
  const createModal = useCreateModal();

  const openContentSetup = useCallback(
    (data) => {
      createModal({
        content: (close) => <ContentSetup data={data} onClose={close} />,
      });
    },
    [createModal],
  );

  return openContentSetup;
};

const validationSchema = yup.object().shape({
  title: yup.string().min(1, 'Too short').max(50, 'Too long'),
  body: yup.string().required('Required'),
});

const hookConfig = {
  add: useCreateContent,
  edit: useUpdateContent,
};

function ContentSetup({ data, onClose }) {
  const { type, category, contents } = data;
  const hook = hookConfig[type];
  const [isLoading, action] = hook(onClose);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: { title: '', body: '' },
    validationSchema,
    onSubmit: (payload) => {
      action({
        categoryId: category?.id,
        image_url: 'https://www.url.com',
        is_active: 1,
        ...payload,
        ...(type === 'edit' && { contentId: head(contents)?.id }),
      });
    },
    validateOnChange: false,
  });

  useEffect(() => {
    if (type !== 'edit') return;
    const content = head(contents);
    setFieldValue('title', content?.title);
    setFieldValue('body', content?.body);
  }, [type, contents, setFieldValue]);

  return (
    <ModalCard
      size="xl max-h-[90vh] overflow-y-auto"
      title={upperCase(`${type} ${category?.name}`)}
      onClose={onClose}
    >
      <Form
        onSubmit={handleSubmit}
        className="px-5 pb-5 w-full space-y-6 flex flex-col justify-between h-[90vh]"
      >
        <div className="flex flex-col h-full gap-6">
          <FormInput
            name="title"
            onSetFieldValue={setFieldValue}
            value={values?.title}
            errors={errors?.title}
            label="Title"
            required
            placeHolder="Enter your text here"
          />
          <div className="flex-1">
            <FormRichText
              name="body"
              onSetFieldValue={setFieldValue}
              value={values?.body}
              label="Description"
              containerClassName="h-full flex flex-col"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            outline
            className="uppercase"
            disabled={isLoading}
          >
            cancel
          </Button>
          <Button
            type="submit"
            primary
            className="uppercase"
            disabled={isLoading}
          >
            {type === 'add' ? 'add' : 'save'}
          </Button>
        </div>
      </Form>
    </ModalCard>
  );
}

ContentSetup.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ContentSetup;
